// The user's Google connection (meetings to tickets): where they
// see it, start it and end it. A row on the account page (`/account`), the
// twin of `GitHubConnection` and `FigmaConnection`, and the ONLY place to
// connect or disconnect Google (owner decision). The meetings pages and
// the Focus "Meetings" button only read whether it is connected.
//
// Everything it shows comes from `GET /api/meetings/status?email=1`:
//
//   - nothing       `enabled: false` or a 404 (meetings are off in this
//                   environment, `onAvailability("off")`), or the read failed
//                   (`"failed"`, so the page can say so).
//   - not connected "Google verbinden", a full-page navigation to
//                   `/auth/google/start` with this page as `returnTo` (the
//                   server keeps only `/account` with its query).
//   - connected     "Verbunden als <email>" (or "Google ist verbunden." when
//                   Google gave no e-mail), a quiet "Neu verbinden" and
//                   "Trennen".
//
// "NEU VERBINDEN" FIXES A DEAD GRANT WITHOUT "TRENNEN". The status sees only
// that Loom holds a row, not whether Google still honours it: a grant
// revoked at Google (`invalid_grant`, the meetings pages read `host_lost`)
// still reads as connected, as GitHub's `usable` does. The same Connect
// navigation with a new consent replaces the row; for the same account the
// server revokes nothing (a revoke at Google would drop the new grant too,
// `apps/server/src/http/google-auth.ts` point 6).
//
// DISCONNECT ASKS FIRST: Loom then can neither start nor process meetings,
// and the way back is a new Google consent. The first click opens a small
// card inside the row; only the second writes `POST /auth/google/disconnect`
// (the server revokes the grant at Google and deletes Loom's copy). Escape
// or Cancel backs out.
//
// A Connect comes back here with the callback's `?google=` marker: the
// outcome line reads it once, and the marker leaves the URL (the `from` the
// page's Back uses stays), so a reload does not show an old error again.
// `URLSearchParams`, never a regex: safari14 has no lookbehind.

import { useCallback, useEffect, useId, useRef, useState } from "react";

import { useT, type MessageKey } from "../i18n/index.js";
import { HttpError } from "../lib/http";
import { useHereLocation } from "../focus/figma/useHereLocation";
import {
  disconnectGoogle,
  googleConnectHref,
  GOOGLE_RESULT_PARAM,
  readMeetingsStatus,
} from "../focus/meetings/meetingApi";
import type { ConnectionAvailability } from "../components/GitHubConnection";

/** The callback's markers (`apps/server/src/http/google-auth.ts`). */
const RESULT_COPY: Readonly<Record<string, MessageKey>> = {
  connected: "meetings.google.connected",
  access_denied: "meetings.google.result.accessDenied",
  oauth_error: "meetings.google.result.oauthError",
  exchange_failed: "meetings.google.result.exchangeFailed",
  missing_scopes: "meetings.google.result.missingScopes",
  domain_refused: "meetings.google.result.domainRefused",
};

const resultKeyOf = (raw: string | null): MessageKey | null =>
  raw !== null && Object.prototype.hasOwnProperty.call(RESULT_COPY, raw)
    ? (RESULT_COPY[raw] ?? null)
    : null;

/** This page's path and query without a stale `google` marker. */
const withoutMarker = (pathname: string, search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(GOOGLE_RESULT_PARAM);
  const rest = params.toString();
  return rest === "" ? pathname : `${pathname}?${rest}`;
};

type View =
  | { readonly kind: "loading" }
  | { readonly kind: "hidden" }
  | { readonly kind: "not_connected" }
  | { readonly kind: "connected"; readonly email: string | null };

type Note = "disconnected" | "failed" | null;

export function GoogleConnection({
  onAvailability,
}: {
  /** Once per settled status read: `"off"` when meetings are off here,
   *  `"failed"` on an error, `"shown"` when the row renders. */
  readonly onAvailability?: (availability: ConnectionAvailability) => void;
}) {
  const t = useT();
  const onAvailabilityRef = useRef(onAvailability);
  onAvailabilityRef.current = onAvailability;
  const { here, replace } = useHereLocation();
  const [resultKey, setResultKey] = useState<MessageKey | null>(() =>
    resultKeyOf(new URLSearchParams(here.search).get(GOOGLE_RESULT_PARAM)),
  );
  const [view, setView] = useState<View>({ kind: "loading" });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<Note>(null);
  const alive = useRef(true);
  const busyRef = useRef(false);
  // A status read that started before a disconnect must not land after it.
  const generation = useRef(0);
  const headId = useId();
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);
  const connectRef = useRef<HTMLAnchorElement | null>(null);
  // Where focus goes on the next commit (as `FigmaConnection` does). Set by
  // the act that moves it, read once by the effect below, so a status reload
  // or a mount never steals focus; the button the user pressed may be gone.
  const focusNext = useRef<"cancel" | "trigger" | "connect" | null>(null);

  // Read the marker once, then drop it from the URL (keeping `from`).
  useEffect(() => {
    const params = new URLSearchParams(here.search);
    if (!params.has(GOOGLE_RESULT_PARAM)) return;
    setResultKey(resultKeyOf(params.get(GOOGLE_RESULT_PARAM)));
    replace(`${withoutMarker(here.pathname, here.search)}${here.hash}`);
  }, [here.pathname, here.search, here.hash, replace]);

  const load = useCallback(async (): Promise<void> => {
    const mine = ++generation.current;
    let next: View;
    let availability: ConnectionAvailability;
    try {
      const status = await readMeetingsStatus({ withEmail: true });
      if (!status.enabled) {
        next = { kind: "hidden" };
        availability = "off";
      } else {
        next =
          status.googleConnected === true
            ? { kind: "connected", email: status.googleEmail ?? null }
            : { kind: "not_connected" };
        availability = "shown";
      }
    } catch (err) {
      next = { kind: "hidden" };
      // The status route always exists; a 404 is a server from before it.
      availability = err instanceof HttpError && err.status === 404 ? "off" : "failed";
    }
    if (alive.current && mine === generation.current) {
      setView(next);
      onAvailabilityRef.current?.(availability);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);

  useEffect(() => {
    const target = focusNext.current;
    if (target === null) return;
    const el =
      target === "cancel"
        ? cancelRef.current
        : target === "trigger"
          ? triggerRef.current
          : connectRef.current;
    if (el === null) return;
    focusNext.current = null;
    el.focus();
  });

  const cancelDisconnect = (): void => {
    if (busyRef.current) return;
    focusNext.current = "trigger";
    setConfirming(false);
  };

  const disconnect = async (): Promise<void> => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setNote(null);
    try {
      await disconnectGoogle();
      if (alive.current) {
        generation.current += 1;
        setView({ kind: "not_connected" });
        setNote("disconnected");
        focusNext.current = "connect";
      }
    } catch {
      if (alive.current) {
        setNote("failed");
        focusNext.current = "trigger";
      }
    } finally {
      busyRef.current = false;
      if (alive.current) {
        setBusy(false);
        setConfirming(false);
      }
    }
    // Converge on what the server now says either way.
    await load();
  };

  if (view.kind === "loading" || view.kind === "hidden") return null;

  const sentence =
    view.kind === "not_connected"
      ? t("meetings.google.notConnected")
      : view.email !== null
        ? t("meetings.google.connectedAs", { email: view.email })
        : t("meetings.google.connected");

  const noteText =
    note === "disconnected"
      ? t("meetings.google.disconnected")
      : note === "failed"
        ? t("meetings.google.disconnectFailed")
        : resultKey !== null && !(resultKey === "meetings.google.connected" && view.kind === "connected")
          ? t(resultKey)
          : "";

  return (
    <section className="account-row" aria-labelledby={headId}>
      <h3 id={headId} className="account-row-title">
        {t("account.google.title")}
      </h3>
      <p className="account-row-status">{sentence}</p>
      <div className="account-row-acts">
        {view.kind === "not_connected" && (
          <a ref={connectRef} className="focus-btn" href={googleConnectHref(withoutMarker(here.pathname, here.search))}>
            {t("meetings.google.connect")}
          </a>
        )}
        {view.kind === "connected" && (
          <a
            className="account-act"
            href={googleConnectHref(withoutMarker(here.pathname, here.search))}
            aria-label={t("meetings.google.reconnectLabel")}
          >
            {t("meetings.google.reconnect")}
          </a>
        )}
        {view.kind === "connected" && !confirming && (
          <button
            ref={triggerRef}
            type="button"
            className="account-act"
            aria-label={t("meetings.google.disconnect")}
            onClick={() => {
              setNote(null);
              setResultKey(null);
              focusNext.current = "cancel";
              setConfirming(true);
            }}
          >
            {t("meetings.google.confirm")}
          </button>
        )}
      </div>
      {confirming && (
        <div
          className="account-row-confirm"
          role="group"
          aria-labelledby={titleId}
          onKeyDown={(e) => {
            if (e.key !== "Escape" || busyRef.current) return;
            e.preventDefault();
            cancelDisconnect();
          }}
        >
          <span id={titleId} className="account-row-confirm-title">
            {t("meetings.google.confirmTitle")}
          </span>
          <span className="account-row-confirm-body">{t("meetings.google.confirmBody")}</span>
          <div className="account-row-confirm-acts">
            <button
              type="button"
              className="focus-btn"
              disabled={busy}
              onClick={() => void disconnect()}
            >
              {t("meetings.google.confirm")}
            </button>
            <button
              ref={cancelRef}
              type="button"
              className="focus-btn is-ghost"
              disabled={busy}
              onClick={cancelDisconnect}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {/* Always mounted, so a screen reader announces the line when it fills. */}
      <div className={"account-row-note" + (noteText === "" ? " is-empty" : "")}>
        <p className="account-row-note-text" role="status">
          {noteText}
        </p>
      </div>
    </section>
  );
}
